import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import CaptureEscKeydown, { getHandler, type OnEscKeydownHandler } from '../capture-esc-keydown';
import Overlay from '../overlay';
import Container from './container';
import type { DialogButton, DialogLinkButton, DialogSize } from './dialog.types';
import Footer from './footer';
import DialogHeader from './header';
export type DialogProps = {
  children?: React.ReactNode;
  container?: HTMLElement | null;
  contentClassName?: string;
  isConfirmation?: boolean;
  isCentered?: boolean;
  linkButton?: DialogLinkButton;
  noPadding?: boolean;
  noScroll?: boolean;
  onClose: OnEscKeydownHandler;
  /** Runs before focus returns to what opened the dialog; `preventDefault()` keeps focus where the dialog's action put it. */
  onCloseAutoFocus?: (event: Event) => void;
  /** Runs as focus moves into the dialog; `preventDefault()` and focus an element yourself to choose where it lands. */
  onOpenAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  open: boolean;
  preventEscapeKeyDown?: boolean;
  primaryButton?: DialogButton;
  secondaryButton?: DialogButton;
  size?: DialogSize;
  title: string;
  width?: React.CSSProperties['width'];
  headerIcon?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
  headerAction?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
  headerSecondaryAction?: {
    component: Icon;
    ariaLabel: string;
    onClick: () => void;
  };
};
const Dialog = forwardRef<HTMLDivElement, DialogProps>((t0, ref) => {
  const $ = _c(58);
  const {
    children,
    container,
    contentClassName,
    headerAction,
    headerSecondaryAction,
    headerIcon,
    isConfirmation: t1,
    isCentered: isCenteredProp,
    linkButton,
    noPadding: t2,
    noScroll: t3,
    onClose,
    onCloseAutoFocus,
    onOpenAutoFocus,
    onEscapeKeyDown,
    open,
    preventEscapeKeyDown,
    primaryButton,
    secondaryButton,
    size: t4,
    title,
    width
  } = t0;
  const isConfirmation = t1 === undefined ? false : t1;
  const noPadding = t2 === undefined ? false : t2;
  const noScroll = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "default" : t4;
  const hasFooter = primaryButton || secondaryButton || linkButton;
  const contentRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  const isCentered = isCenteredProp ?? isConfirmation;
  let t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const checkScroll = () => {
        if (contentRef.current) {
          const {
            scrollHeight,
            clientHeight,
            scrollTop
          } = contentRef.current;
          const hasMoreContent = scrollHeight > clientHeight;
          const reachedBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;
          setHasScroll(hasMoreContent && !reachedBottom);
        }
      };
      const contentElement = contentRef.current;
      if (contentElement) {
        checkScroll();
        contentElement.addEventListener("scroll", checkScroll);
        window.addEventListener("resize", checkScroll);
        const observer = new ResizeObserver(checkScroll);
        observer.observe(contentElement);
        return () => {
          contentElement.removeEventListener("scroll", checkScroll);
          window.removeEventListener("resize", checkScroll);
          observer.disconnect();
        };
      }
    };
    t6 = [];
    $[0] = t5;
    $[1] = t6;
  } else {
    t5 = $[0];
    t6 = $[1];
  }
  useEffect(t5, t6);
  let t7;
  if ($[2] !== onClose) {
    t7 = getHandler(onClose);
    $[2] = onClose;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const handleClose = t7;
  let t8;
  if ($[4] !== isConfirmation || $[5] !== open) {
    t8 = <RadixDialog.Overlay asChild={true}><Overlay isVisible={open} isConfirmation={isConfirmation} /></RadixDialog.Overlay>;
    $[4] = isConfirmation;
    $[5] = open;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== width) {
    t9 = {
      width
    };
    $[7] = width;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== handleClose || $[10] !== headerAction || $[11] !== headerIcon || $[12] !== headerSecondaryAction || $[13] !== title) {
    t10 = <DialogHeader title={title} onClose={handleClose} icon={headerIcon} action={headerAction} secondaryAction={headerSecondaryAction} />;
    $[9] = handleClose;
    $[10] = headerAction;
    $[11] = headerIcon;
    $[12] = headerSecondaryAction;
    $[13] = title;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = !noPadding;
  let t12;
  if ($[15] !== contentClassName || $[16] !== noPadding || $[17] !== noScroll || $[18] !== t11) {
    t12 = cx("flex-1 overflow-auto min-h-0", {
      "p-0": noPadding,
      "p-6": t11,
      "overflow-hidden": noScroll
    }, contentClassName);
    $[15] = contentClassName;
    $[16] = noPadding;
    $[17] = noScroll;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== onClose || $[21] !== title) {
    t13 = <CaptureEscKeydown id={title} onEscKeydown={onClose} />;
    $[20] = onClose;
    $[21] = title;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== children || $[24] !== t12 || $[25] !== t13) {
    t14 = <div ref={contentRef} className={t12}>{t13}{children}</div>;
    $[23] = children;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== hasFooter || $[28] !== hasScroll || $[29] !== linkButton || $[30] !== primaryButton || $[31] !== secondaryButton || $[32] !== size) {
    t15 = hasFooter && <Footer hasScroll={hasScroll} linkButton={linkButton} primaryButton={primaryButton} secondaryButton={secondaryButton} size={size} />;
    $[27] = hasFooter;
    $[28] = hasScroll;
    $[29] = linkButton;
    $[30] = primaryButton;
    $[31] = secondaryButton;
    $[32] = size;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== handleClose || $[35] !== isCentered || $[36] !== isConfirmation || $[37] !== onCloseAutoFocus || $[38] !== onEscapeKeyDown || $[39] !== onOpenAutoFocus || $[40] !== open || $[41] !== preventEscapeKeyDown || $[42] !== ref || $[43] !== size || $[44] !== t10 || $[45] !== t14 || $[46] !== t15 || $[47] !== t9 || $[48] !== title) {
    t16 = <Container aria-label={title} aria-describedby={undefined} isConfirmation={isConfirmation} isCentered={isCentered} onClose={handleClose} onCloseAutoFocus={onCloseAutoFocus} onOpenAutoFocus={onOpenAutoFocus} onEscapeKeyDown={onEscapeKeyDown} preventEscapeKeyDown={preventEscapeKeyDown} ref={ref} size={size} style={t9} open={open}>{t10}{t14}{t15}</Container>;
    $[34] = handleClose;
    $[35] = isCentered;
    $[36] = isConfirmation;
    $[37] = onCloseAutoFocus;
    $[38] = onEscapeKeyDown;
    $[39] = onOpenAutoFocus;
    $[40] = open;
    $[41] = preventEscapeKeyDown;
    $[42] = ref;
    $[43] = size;
    $[44] = t10;
    $[45] = t14;
    $[46] = t15;
    $[47] = t9;
    $[48] = title;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== container || $[51] !== t16 || $[52] !== t8) {
    t17 = <RadixDialog.Portal container={container}>{t8}{t16}</RadixDialog.Portal>;
    $[50] = container;
    $[51] = t16;
    $[52] = t8;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== handleClose || $[55] !== open || $[56] !== t17) {
    t18 = <RadixDialog.Root open={open} onOpenChange={handleClose} modal={true}>{t17}</RadixDialog.Root>;
    $[54] = handleClose;
    $[55] = open;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  return t18;
});
export default Dialog;